import { useState, useCallback, useRef, useEffect } from 'react';
import { productosService } from '@/services/api/productos.service';
import type { ProductoBuscarDto } from '@/services/api/productos.service';

const DEBOUNCE_MS = 300;

export interface UseProductSearchOptions {
  establecimientoId?: string;
  limit?: number;
  soloBienes?: boolean;
}

export interface UseProductSearchResult {
  results: ProductoBuscarDto[];
  isSearching: boolean;
  search: (term: string) => void;
  clear: () => void;
}

export function useProductSearch({
  establecimientoId,
  limit = 20,
  soloBienes = false,
}: UseProductSearchOptions = {}): UseProductSearchResult {
  const [results, setResults] = useState<ProductoBuscarDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const search = useCallback(
    (term: string) => {
      clearTimeout(debounceRef.current);

      if (!term.trim()) {
        setResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      debounceRef.current = setTimeout(async () => {
        try {
          const res = await productosService.buscar({
            busqueda: term.trim(),
            establecimientoId: establecimientoId || undefined,
            limit,
            soloBienes: soloBienes || undefined,
          });
          setResults(res.ok && Array.isArray(res.data) ? res.data : []);
        } catch {
          setResults([]);
        } finally {
          setIsSearching(false);
        }
      }, DEBOUNCE_MS);
    },
    [establecimientoId, limit, soloBienes],
  );

  const clear = useCallback(() => {
    clearTimeout(debounceRef.current);
    setResults([]);
    setIsSearching(false);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return { results, isSearching, search, clear };
}
